import React, { useState, useCallback, useEffect } from 'react';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import type { ContentProps, TriggerProps } from '@atlaskit/popup';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { CustomItem } from '@atlaskit/side-navigation';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useLeftSidebarController,
	useLeftSidebarState,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useSimplifyNavExperimentActions } from '@atlassian/jira-onboarding-simplify-navigation-experiment/src/common/controllers/add-shortcut/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { MANAGE_FEATURE_MENU_ITEM_TEST_ID } from '../common/constants.tsx';
import { PopupTriggerMenuItem } from '../common/ui/popup-trigger-menu-item/index.tsx';
import {
	DependencyModalProvider,
	useDependencyModalContext,
} from '../controllers/dependency-modal-context/index.tsx';
import { useFeatureToggleProps } from '../controllers/index.tsx';
import { useShouldApplyFeatureToggle } from '../controllers/use-should-apply-tmp-feature-toggle/index.tsx';
import { useShowFlags } from '../controllers/use-show-flags/index.tsx';
import { DisablePrerequisiteModal } from './disable-prerequisite-modal/index.tsx';
import { FeaturePopupContent } from './feature-popup-content/index.tsx';
import messages from './messages.tsx';
import type { AddFeatureWithDataProps } from './types.tsx';

export const AddFeatureWithData = ({
	featureStatusList,
	projectUrl,
	boardId,
	onNavigateToFeaturesSetting,
}: AddFeatureWithDataProps) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { formatMessage } = useIntl();
	const { showErrorFlag } = useShowFlags(projectUrl);
	const { isFetchError, shouldSyncFeatures } = useFeatureToggleProps({
		boardId,
		featureStatusList,
	});
	const { data: modalData } = useDependencyModalContext();
	const { isCollapsed } = useLeftSidebarState();
	const { expandLeftSidebar } = useLeftSidebarController();
	const { setIsModalOpenViaAddView } = useSimplifyNavExperimentActions();
	const ufoPressTracing = usePressTracing('next-gen-navigator-feature-toggle-popup-opened');
	const handleFeatureListPopupClose = useCallback(() => {
		shouldSyncFeatures.current = true;
		setIsOpen(false);
	}, [shouldSyncFeatures]);

	const onManageFeatureClick = useCallback(() => {
		if (isFetchError) {
			showErrorFlag();
		} else {
			ufoPressTracing();
			shouldSyncFeatures.current = false;
			// Expand the sidebar is it is collapsed
			if (isCollapsed) {
				expandLeftSidebar();
			}
			setIsOpen(!isOpen);
		}
	}, [
		isFetchError,
		showErrorFlag,
		ufoPressTracing,
		shouldSyncFeatures,
		isCollapsed,
		isOpen,
		expandLeftSidebar,
	]);

	const renderTriggerItem = useCallback(
		(triggerProps: TriggerProps) => (
			<MenuItem
				{...triggerProps}
				analytics={{ itemId: 'addView' }}
				onClick={onManageFeatureClick}
				iconBefore={<EditorAddIcon spacing="spacious" label="" />}
				data-testid={MANAGE_FEATURE_MENU_ITEM_TEST_ID}
				overrides={{
					CustomItem: {
						render: (props) => <CustomItem {...props} component={PopupTriggerMenuItem} />,
					},
				}}
			>
				{formatMessage(messages.addView)}
			</MenuItem>
		),
		[formatMessage, onManageFeatureClick],
	);

	const renderContent = useCallback(
		({ onClose, ...props }: ContentProps) => (
			<FeaturePopupContent
				boardId={boardId}
				projectUrl={projectUrl}
				onNavigateToFeaturesSetting={(e) => {
					onNavigateToFeaturesSetting(e);
					onClose?.(e);
				}}
				onAddShortcutClick={(e) => {
					onClose?.(e);
					// add shortcut modal requires the FeaturePopupContent to be closed.
					setIsModalOpenViaAddView(true);
				}}
				{...props}
			/>
		),
		[boardId, onNavigateToFeaturesSetting, projectUrl, setIsModalOpenViaAddView],
	);

	useEffect(() => {
		if (isCollapsed) {
			setIsOpen(false);
		}
	}, [isCollapsed]);

	return (
		<>
			<Popup
				offset={[0, 16]}
				isOpen={isOpen}
				placement="right-start"
				messageId="tmp-feature-toggle.ui.popup"
				messageType="transactional"
				content={renderContent}
				onClose={handleFeatureListPopupClose}
				trigger={renderTriggerItem}
			/>
			{modalData?.featureToDisable && modalData.beneficiaryFeature && (
				<ContextualAnalyticsData sourceType={MODAL} sourceName="disablePrerequisite">
					<DisablePrerequisiteModal boardId={boardId} projectUrl={projectUrl} />
					<FireScreenAnalytics />
				</ContextualAnalyticsData>
			)}
		</>
	);
};
export const AddFeature = ({
	featureStatusList,
	projectUrl,
	boardId,
	onNavigateToFeaturesSetting,
}: AddFeatureWithDataProps) => {
	const shouldApplyFeatureToggle = useShouldApplyFeatureToggle();

	return (
		<JSErrorBoundary
			fallback="unmount"
			id="jiraTmpFeatureToggle"
			packageName="jiraTmpFeatureToggle"
			teamName="jlove-makkuro"
			sendToPrivacyUnsafeSplunk
		>
			{shouldApplyFeatureToggle && (
				<DependencyModalProvider>
					<ContextualAnalyticsData attributes={{ boardId }}>
						<AddFeatureWithData
							featureStatusList={featureStatusList}
							boardId={boardId}
							projectUrl={projectUrl}
							onNavigateToFeaturesSetting={onNavigateToFeaturesSetting}
						/>
					</ContextualAnalyticsData>
				</DependencyModalProvider>
			)}
		</JSErrorBoundary>
	);
};
